import { useState } from 'react';
import { isLocale, type Locale } from '@st/i18n';
import { useMe, useUpdateMe } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { NotificationSettings } from './NotificationSettings';
import { BrowserNotifications } from '../../components/BrowserNotifications';
import { InstallApp } from '../../components/InstallApp';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { useAuth } from '../../auth/AuthProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  SkeletonCard,
  Switch,
  useToast,
} from '../../ui';

const LOCALE_LABEL: Record<Locale, string> = { hr: 'Hrvatski', en: 'English' };

export function Profile() {
  const { t, locale, setLocale } = useTranslation();
  const { logout, patchUser, setApiLocale } = useAuth();
  const query = useMe();
  const update = useUpdateMe();
  const { toast } = useToast();
  const [phone, setPhone] = useState<string | null>(null);

  async function save(patch: Record<string, unknown>) {
    try {
      await update.mutateAsync(patch);
      toast(t('profile.saved'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function changeLocale(next: string) {
    if (!isLocale(next)) return;
    setLocale(next);
    setApiLocale(next);
    patchUser({ locale: next });
    await save({ locale: next });
  }

  return (
    <>
      <PageHeader title={t('profile.title')} />
      <QueryBoundary query={query} skeleton={<SkeletonCard />}>
        {(me) => (
          <div className="flex flex-col gap-5">
            <Card className="flex items-center gap-4">
              <Avatar name={`${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() || me.email} size="lg" />
              <div className="min-w-0">
                <p className="font-display text-[24px] font-semibold text-ink">
                  {[me.firstName, me.lastName].filter(Boolean).join(' ') || me.email}
                </p>
                <p className="truncate text-[13px] text-ink-muted">{me.email}</p>
                {me.trainerName && (
                  <p className="mt-1 text-[13px] text-ink-muted">{me.trainerName}</p>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader title={t('profile.personal')} />
              <div className="flex flex-col gap-4">
                <Input id="p-first" label={t('auth.firstName')} defaultValue={me.firstName ?? ''} readOnly />
                <Input id="p-last" label={t('auth.lastName')} defaultValue={me.lastName ?? ''} readOnly />
                <Input
                  id="p-phone"
                  label="Telefon"
                  value={phone ?? me.phone ?? ''}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <Button
                  className="self-start"
                  size="sm"
                  loading={update.isPending}
                  onClick={() => void save({ phone })}
                >
                  {t('common.save')}
                </Button>
              </div>
            </Card>

            <Card>
              <CardHeader title={t('profile.preferences')} />
              <div className="flex flex-col gap-5">
                <Select
                  id="p-locale"
                  label={t('shell.language')}
                  value={locale}
                  onChange={(e) => void changeLocale(e.target.value)}
                  options={[
                    { value: 'hr', label: LOCALE_LABEL.hr },
                    { value: 'en', label: LOCALE_LABEL.en },
                  ]}
                />
                <Switch
                  id="p-units"
                  label={t('profile.units')}
                  description={me.unitSystem === 'metric' ? t('profile.metric') : t('profile.imperial')}
                  checked={me.unitSystem === 'metric'}
                  onCheckedChange={(v) =>
                    void save({ unitSystem: v ? 'metric' : 'imperial' })
                  }
                />
                <Input id="p-tz" label={t('profile.timezone')} defaultValue={me.timezone} readOnly />
              </div>
            </Card>

            <NotificationSettings />

            <BrowserNotifications />

            <InstallApp />

            <Button
              variant="secondary"
              className="self-start"
              onClick={() => void logout()}
            >
              {t('shell.logout')}
            </Button>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
